import { ContainerErrorType, ContainerWarning, IContainer, IErrorBase } from '@fluidframework/container-definitions';
import { EventEmitter } from 'events';

import { logVerbose } from '../log/utility';
import { MfsErrorType } from './mfsErrorType';
import { isFluidGenericError, isFluidRuntimeError, isFluidThrottlingError } from './mfsFluidErrorMapping';

/** The Fluid runtime events raised by the `MfsClient` instances. */
export type MfsClientEvents = {
  fluidConnected: { clientId: string };
  fluidDisconnected: Record<string, never>;
  fluidThrottling: { message: string; retryAfterSeconds: number };
  fluidDirty: { isDirty: boolean };
  fluidSaved: { isDirty: boolean };
  fluidReadOnly: { isReadOnly: boolean };
  readyToWrite: { podId: string };
  error: { errorType: MfsErrorType; message: string; error?: unknown };
  recoveryStart: { podId: string };
  recoveryEnd: { podId: string; successfulRecovery: boolean };
  readyToRead: { podId: string };
};

export interface MfsClientEventEmitter {
  on<E extends keyof MfsClientEvents>(event: E, f: (arg: MfsClientEvents[E]) => void): void;
  off<E extends keyof MfsClientEvents>(event: E, f: (arg: MfsClientEvents[E]) => void): void;
}

/** An event emitter for the Mfs Clients. */
export class MfsClientEventEmitterBase extends EventEmitter implements MfsClientEventEmitter {
  /**
   * Emits the given `MfsClientEvent`.
   *
   * @param event The event to emit.
   * @param arg The args for the event
   */
  public emitMfsClientEvent<E extends keyof MfsClientEvents>(event: E, arg: MfsClientEvents[E]): boolean {
    logVerbose('emitMfsClientEvent', 'Emitting', { event, arg });
    return this.emit(event, arg);
  }

  /**
   * Attaches the event listeners to the given Fluid container, so that relevant `MfsClient` events can be emitted for
   * certain container events.
   *
   * @param container The Fluid container to attach.
   * @param connectionTimeoutMs The milliseconds after which the container is considered disconnected.
   */
  public attachEventListeners(container: IContainer, connectionTimeoutMs: number): void {
    this.attachConnectionStateListeners(container, connectionTimeoutMs);
    this.attachErrorListeners(container);
    this.attachRuntimeListeners(container);
  }

  /**
   * Listens to the connection state events of the Fluid container & raises `fluidDisconnected` if the container doesn't
   * get `connected` within `connectionTimeoutMs` milliseconds after it gets `disconnected`.
   *
   * @param container The Fluid container to attach.
   * @param connectionTimeoutMs The milliseconds after which the container is considered disconnected.
   */
  private attachConnectionStateListeners(container: IContainer, connectionTimeoutMs: number): void {
    let timeoutId: NodeJS.Timeout | undefined;

    container.on('connected', (clientId: string) => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      this.emitMfsClientEvent('fluidConnected', { clientId: clientId });
    });

    container.on('disconnected', () => {
      timeoutId = setTimeout(() => {
        this.emit('fluidDisconnected');
      }, connectionTimeoutMs);
    });
  }

  /**
   * Attaches the error listeners to the given Fluid container, so that relevant `MfsClient` events can be emitted for
   * certain container events. ref: @fluidframework\container-definitions\src\error.ts
   *
   * @param container The Fluid container to attach.
   */
  private attachErrorListeners(container: IContainer): void {
    container.on('closed', (error?: IErrorBase) => {
      if (error) {
        if (isFluidThrottlingError(error)) {
          this.emitMfsClientEvent('fluidThrottling', {
            message: error.message,
            retryAfterSeconds: error.retryAfterSeconds,
          });
        }

        if (isFluidGenericError(error)) {
          this.emitMfsClientEvent('error', {
            errorType: 'fluidGenericError',
            error: error.error,
            message: `${error.errorType}:${error.message}`,
          });
        }

        if (isFluidRuntimeError(error)) {
          this.emitMfsClientEvent('error', {
            errorType:
              error.errorType === ContainerErrorType.dataCorruptionError
                ? 'fluidDataCorruption'
                : 'fluidDataProcessing',
            message: error.message,
          });
        }
      } else {
        // there's no error, which means the container was closed by the host.
        // This shouldn't happen under normal circumstances because MFS doesn't return the container to the host app.
        this.emitMfsClientEvent('error', { errorType: 'exception', message: 'Container was closed.' });
      }
    });

    // container errors are also propagated as warnings.
    container.on('warning', (error: ContainerWarning) => {
      this.emitMfsClientEvent('error', {
        errorType: 'fluidWarning',
        message: `${error.errorType}:${error.message}`,
      });
    });
  }

  /**
   * Listens to the rest of the loader state events of the Fluid container & raises the appropriate client events. These
   * include the dirty, saved, and readonly.
   *
   * @param container The Fluid container to attach.
   */
  private attachRuntimeListeners(container: IContainer): void {
    container.on('dirty', (isDirty: boolean) => {
      this.emitMfsClientEvent('fluidDirty', { isDirty });
    });

    container.on('saved', (isDirty: boolean) => {
      this.emitMfsClientEvent('fluidSaved', { isDirty });
    });

    container.on('readonly', (isReadOnly: boolean) => {
      this.emitMfsClientEvent('fluidReadOnly', { isReadOnly });
    });
  }
}
